import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { Button, NavArrowLeftIcon, SettingsIcon } from 'examkrackers-components'

import QuizDiagnosticsChapterFilter from './QuizDiagnosticsChapterFilter'
import AllRedDotsToggle from './AllRedDotsToggle'
import SrqRedDotsSettingsModal from './SrqRedDotsSettingsModal'
import SpaceRepetitionModal from 'modules/books/components/aiSpaceRepetition/SpaceRepetitionModal'
import infoCircleIcon from 'assets/info-circle.svg'
import { ChapterViewSection } from '../chapterView/types'
import { ChapterFilterOption } from '../utils/filterQuizDiagnosticsData'
import { QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH } from '../utils/quizDiagnosticsBreakpoints'
import { useSelectQuizDiagnosticsChapter } from '../hooks/useSelectQuizDiagnosticsChapter'
import { getSrqTimezone } from '../utils/getSrqApiParams'
import {
  getSharedSrqStatus,
  patchSharedSrqStatus,
  useSrqStatus
} from '../utils/srqStatusStore'
import {
  updateChapterSrqRedDotsVisibility,
  updateCourseSrqRedDotsVisibility
} from 'services/CourseService'
import { getCurrentCourse } from 'modules/courses/ducks/selectors'
import { Course } from 'types'

type ChapterQuizContext = {
  bookTitle: string
  chapterOrder: number
}

type QuizDiagnosticsHeaderProps = {
  quizCount: number
  filterOptions: ChapterFilterOption[]
  isChapterView?: boolean
  chapterQuizContext?: ChapterQuizContext | null
  chapterId?: string
  tag?: string
  chapterOrder?: number
  sections?: ChapterViewSection[]
  chapterSrqRedDotsVisible?: boolean
  onAfterRedDotsToggle?: () => void
  onChapterVisibilityChange?: (visible: boolean) => void
}

const QUIZ_COUNT_DARK_RED = '#B71C1C'
const CONTROL_SEPARATOR_COLOR = '#7B7C7C'

const QuizDiagnosticsHeader = ({
  quizCount,
  filterOptions,
  isChapterView = false,
  chapterQuizContext = null,
  chapterId = '',
  tag = '',
  chapterOrder = 0,
  sections = [],
  chapterSrqRedDotsVisible = true,
  onAfterRedDotsToggle,
  onChapterVisibilityChange
}: QuizDiagnosticsHeaderProps): JSX.Element => {
  const { t } = useTranslation()
  const { selectAllChapters } = useSelectQuizDiagnosticsChapter()
  const currentCourse: Course = useSelector(getCurrentCourse)
  const { srqRedDotsVisible } = useSrqStatus()
  const [isUpdatingRedDots, setIsUpdatingRedDots] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isHowToUseOpen, setIsHowToUseOpen] = useState(false)

  const backButtonLabel = t('quizDiagnostics.chapterView.backToChapterAverages')
  const settingsButtonLabel = t('quizDiagnostics.settings')
  const howToUseLabel = t('quizDiagnostics.howToUse')
  const howToUseAriaLabel = t('quizDiagnostics.howToUseAriaLabel')
  const chapterRedDotsTitle = t('quizDiagnostics.chapterRedDots', {
    tag,
    order: chapterOrder
  })

  const handleBackToChapterAverages = () => {
    selectAllChapters()
  }

  const handleOpenSettings = () => {
    setIsSettingsOpen(true)
  }

  const handleCloseSettings = () => {
    setIsSettingsOpen(false)
  }

  const handleOpenHowToUse = () => {
    setIsHowToUseOpen(true)
  }

  const handleCloseHowToUse = () => {
    setIsHowToUseOpen(false)
  }

  const handleHowToUseKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    handleOpenHowToUse()
  }

  const handleAllRedDotsToggle = async () => {
    const courseId = currentCourse?.id
    if (!courseId || isUpdatingRedDots) {
      return
    }

    const previousStatus = getSharedSrqStatus()
    const previousVisible = previousStatus.srqRedDotsVisible
    const previousDueToday = previousStatus.quizzesDueToday
    const nextVisible = !previousVisible

    patchSharedSrqStatus({
      srqRedDotsVisible: nextVisible,
      ...(nextVisible ? {} : { quizzesDueToday: 0 })
    })

    setIsUpdatingRedDots(true)

    try {
      const response = await updateCourseSrqRedDotsVisibility(courseId, {
        visible: nextVisible,
        timezone: getSrqTimezone()
      })
      const responseData = response?.data as
        | {
            srq_red_dots_visible?: boolean
            quizzes_due_today?: number
          }
        | undefined

      patchSharedSrqStatus({
        srqRedDotsVisible: responseData?.srq_red_dots_visible ?? nextVisible,
        quizzesDueToday:
          typeof responseData?.quizzes_due_today === 'number'
            ? responseData.quizzes_due_today
            : nextVisible
            ? previousDueToday
            : 0
      })

      onAfterRedDotsToggle?.()
    } catch (error) {
      console.error('Failed to update course SRQ red dots visibility', error)
      patchSharedSrqStatus({
        srqRedDotsVisible: previousVisible,
        quizzesDueToday: previousDueToday
      })
    } finally {
      setIsUpdatingRedDots(false)
    }
  }

  const handleChapterRedDotsToggle = async () => {
    if (!chapterId || isUpdatingRedDots) {
      return
    }

    const previousStatus = getSharedSrqStatus()
    const previousDueToday = previousStatus.quizzesDueToday
    const previousChapterVisible = chapterSrqRedDotsVisible
    const nextVisible = !previousChapterVisible

    onChapterVisibilityChange?.(nextVisible)
    setIsUpdatingRedDots(true)

    try {
      const response = await updateChapterSrqRedDotsVisibility(chapterId, {
        visible: nextVisible,
        timezone: getSrqTimezone()
      })
      const responseData = response?.data as
        | {
            srq_red_dots_visible?: boolean
            srq_red_dots_effective?: boolean
            quizzes_due_today?: number
          }
        | undefined

      patchSharedSrqStatus({
        quizzesDueToday:
          typeof responseData?.quizzes_due_today === 'number'
            ? responseData.quizzes_due_today
            : previousDueToday
      })

      onChapterVisibilityChange?.(
        responseData?.srq_red_dots_effective ??
          responseData?.srq_red_dots_visible ??
          nextVisible
      )
      onAfterRedDotsToggle?.()
    } catch (error) {
      console.error('Failed to update chapter SRQ red dots visibility', error)
      onChapterVisibilityChange?.(previousChapterVisible)
      patchSharedSrqStatus({
        quizzesDueToday: previousDueToday
      })
    } finally {
      setIsUpdatingRedDots(false)
    }
  }

  const renderQuizCountMessage = () => {
    if (quizCount <= 0) {
      return t('quizDiagnostics.quizCountEmpty')
    }

    if (chapterQuizContext) {
      return (
        <Trans
          i18nKey='quizDiagnostics.quizCountChapter'
          values={{
            count: quizCount,
            bookTitle: chapterQuizContext.bookTitle,
            chapterOrder: chapterQuizContext.chapterOrder
          }}
          components={{
            quizCount: <QuizCountHighlight />
          }}
        />
      )
    }

    return (
      <Trans
        i18nKey='quizDiagnostics.quizCount'
        values={{ count: quizCount }}
        components={{
          quizCount: <QuizCountHighlight />
        }}
      />
    )
  }

  return (
    <HeaderContent $isChapterView={isChapterView}>
      <TextColumn>
        <TitleTextGroup>
          <PageTitle>
            {t(
              isChapterView
                ? 'quizDiagnostics.chapterTitle'
                : 'quizDiagnostics.title'
            )}
          </PageTitle>
          <HowToUseButton
            type='button'
            aria-label={howToUseAriaLabel}
            tabIndex={0}
            onClick={handleOpenHowToUse}
            onKeyDown={handleHowToUseKeyDown}
          >
            <HowToUseIcon src={infoCircleIcon} alt='' aria-hidden='true' />
            <HowToUseLabel>{howToUseLabel}</HowToUseLabel>
          </HowToUseButton>
        </TitleTextGroup>
        <StatusMessage>{renderQuizCountMessage()}</StatusMessage>
      </TextColumn>

      <ControlSeparator aria-hidden='true' />

      <ControlsColumn $isChapterView={isChapterView}>
        <ChapterFilterSlot>
          <QuizDiagnosticsChapterFilter filterOptions={filterOptions} />
        </ChapterFilterSlot>
        <ControlSeparator aria-hidden='true' />
        {isChapterView ? (
          <>
            <SectionRemindersGroup>
              <RedDotsToggleSlot>
                <AllRedDotsToggle
                  isVisible={chapterSrqRedDotsVisible}
                  disabled={isUpdatingRedDots || !chapterId}
                  titleLabel={chapterRedDotsTitle}
                  hideLabel={t('quizDiagnostics.allRedDotsHide')}
                  showLabel={t('quizDiagnostics.allRedDotsShow')}
                  ariaLabel={chapterRedDotsTitle}
                  switchId='chapter-red-dots'
                  onToggle={handleChapterRedDotsToggle}
                />
              </RedDotsToggleSlot>
              <QuizSettingsButton
                type='button'
                onClick={handleOpenSettings}
                aria-label={settingsButtonLabel}
                tabIndex={0}
              >
                <SettingsIconWrapper aria-hidden='true'>
                  <SettingsIcon />
                </SettingsIconWrapper>
                <QuizSettingsLabel>{settingsButtonLabel}</QuizSettingsLabel>
              </QuizSettingsButton>
            </SectionRemindersGroup>
            <ControlSeparator aria-hidden='true' />
            <BackButtonSlot>
              <BackButton
                color='tertiary'
                variant='contained'
                startIcon={<NavArrowLeftIcon />}
                onClick={handleBackToChapterAverages}
                aria-label={backButtonLabel}
              >
                {backButtonLabel}
              </BackButton>
            </BackButtonSlot>
          </>
        ) : (
          <RedDotsToggleSlot>
            <AllRedDotsToggle
              isVisible={srqRedDotsVisible}
              disabled={isUpdatingRedDots}
              titleLabel={t('quizDiagnostics.allRedDots')}
              hideLabel={t('quizDiagnostics.allRedDotsHide')}
              showLabel={t('quizDiagnostics.allRedDotsShow')}
              ariaLabel={t('quizDiagnostics.allRedDots')}
              onToggle={handleAllRedDotsToggle}
            />
          </RedDotsToggleSlot>
        )}
      </ControlsColumn>
      {isHowToUseOpen ? (
        <SpaceRepetitionModal
          isOpen={isHowToUseOpen}
          onClose={handleCloseHowToUse}
          isInfoOnly
        />
      ) : null}
      {isChapterView && isSettingsOpen ? (
        <SrqRedDotsSettingsModal
          open={isSettingsOpen}
          chapterId={chapterId}
          tag={tag}
          chapterOrder={chapterOrder}
          sections={sections}
          chapterSrqRedDotsVisible={chapterSrqRedDotsVisible}
          courseSrqRedDotsVisible={srqRedDotsVisible}
          onClose={handleCloseSettings}
          onSaved={result => {
            if (typeof result?.chapterSrqRedDotsVisible === 'boolean') {
              onChapterVisibilityChange?.(result.chapterSrqRedDotsVisible)
            }
            onAfterRedDotsToggle?.()
          }}
        />
      ) : null}
    </HeaderContent>
  )
}

export default QuizDiagnosticsHeader

const HeaderContent = styled.header<{ $isChapterView: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: ${({ $isChapterView }) =>
    $isChapterView ? 'flex-start' : 'center'};
  justify-content: flex-start;
  width: 100%;
  margin-bottom: 16px;
  box-sizing: border-box;
  position: relative;
  z-index: ${({ theme }) => theme.zIndex.menu};
  gap: ${({ $isChapterView }) => ($isChapterView ? '16px' : '0')};

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  flex: 0 1 auto;
`

const TitleTextGroup = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: baseline;
  min-width: 0;
  gap: 0;
`

const BACK_BUTTON_WIDTH = 147
const BACK_BUTTON_HEIGHT = 32
const HEADER_SECTION_MARGIN = 32

const BackButton = styled(Button)`
  && {
    width: ${BACK_BUTTON_WIDTH}px;
    min-width: ${BACK_BUTTON_WIDTH}px;
    max-width: ${BACK_BUTTON_WIDTH}px;
    height: ${BACK_BUTTON_HEIGHT}px;
    min-height: ${BACK_BUTTON_HEIGHT}px;
    max-height: ${BACK_BUTTON_HEIGHT}px;
    flex-shrink: 0;
    align-self: flex-start;
    margin: 0;
    white-space: nowrap;
    box-sizing: border-box;
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 8px;
    padding-right: 8px;
    line-height: ${BACK_BUTTON_HEIGHT}px;
    font-size: 14px;
  }
`

const BackButtonSlot = styled.div`
  display: flex;
  flex-shrink: 0;
  align-self: flex-start;
  margin-left: auto;
  padding-left: ${HEADER_SECTION_MARGIN}px;
`

const ControlsColumn = styled.div<{ $isChapterView: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: ${({ $isChapterView }) =>
    $isChapterView ? 'flex-start' : 'center'};
  flex: ${({ $isChapterView }) => ($isChapterView ? '1 1 auto' : '0 0 auto')};
  min-width: 0;
  margin-left: 0;
  gap: 0;

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    margin-left: 0;
    width: 100%;
    flex: 1 1 auto;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 12px;
  }
`

const ChapterFilterSlot = styled.div`
  display: flex;
  flex-shrink: 0;
  margin-left: ${HEADER_SECTION_MARGIN}px;
  margin-right: ${HEADER_SECTION_MARGIN}px;

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    margin-left: 0;
    margin-right: 0;
  }
`

const RedDotsToggleSlot = styled.div`
  display: flex;
  flex-shrink: 0;
  margin-left: ${HEADER_SECTION_MARGIN}px;

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    margin-left: 0;
  }
`

const SectionRemindersGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-shrink: 0;
  gap: 0;
`

const ControlSeparator = styled.span`
  display: inline-block;
  width: 1px;
  height: 50px;
  flex-shrink: 0;
  align-self: center;
  margin: 0;
  background: ${CONTROL_SEPARATOR_COLOR};

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    display: none;
  }
`

const HowToUseButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 ${HEADER_SECTION_MARGIN}px 0 12px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  box-sizing: border-box;

  &:focus-visible {
    outline: 2px solid #f8961a;
    outline-offset: 2px;
  }

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    margin: 0 0 0 12px;
  }
`

const HowToUseIcon = styled.img`
  display: block;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`

const HowToUseLabel = styled.span`
  font-family: Arial, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  color: #444444;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-thickness: 1px;
  text-decoration-color: #444444;
  text-underline-offset: 2px;
  white-space: nowrap;
`

const QuizSettingsButton = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  margin: 0 ${HEADER_SECTION_MARGIN}px 0 ${HEADER_SECTION_MARGIN}px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  box-sizing: border-box;

  &:focus-visible {
    outline: 2px solid #f8961a;
    outline-offset: 2px;
  }

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    margin: 0;
  }
`

const SettingsIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: #444444;

  svg {
    width: 20px;
    height: 20px;
  }
`

const QuizSettingsLabel = styled.span`
  font-family: Arial, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  color: #444444;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-thickness: 1px;
  text-decoration-color: #444444;
  text-underline-offset: 2px;
  white-space: nowrap;
`

const PageTitle = styled.h1`
  margin: 0;
  padding: 0;
  width: auto;
  max-width: 100%;
  flex: 0 1 auto;
  font-family: Arial;
  font-weight: 700;
  font-size: 22px;
  line-height: 1.2;
  color: #1a1a1a;
  white-space: nowrap;
`

const StatusMessage = styled.p`
  margin: 6px 0 0;
  padding: 0;
  font-family: Arial;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.3;
  color: #444444;
`

const QuizCountHighlight = styled.span`
  color: ${QUIZ_COUNT_DARK_RED};
  font-weight: 700;
`
